import React, { useCallback, useEffect, useRef } from "react"

import { Skeleton } from "@coval/ui/skeleton"
import { Tooltip } from "@coval/ui/tooltip"

import { useWavesurfer } from "@/components/shared/audio/WaveSurfer"

import {
  useAudioSignedUrlTransport,
  usePublicAudioSignedUrlTransport,
} from "../routes/useAudioTransport"

interface SimpleAudioPlayerProps {
  bucket: string
  audioKey: string

  compact?: boolean
  asTestCase?: boolean
  isPublic?: boolean
}

export const SimpleAudioPlayer: React.FC<SimpleAudioPlayerProps> = ({
  bucket,
  audioKey,
  compact = false,
  asTestCase = false,
  isPublic = false,
}) => {
  const queryInput = { bucket, audioKey, asTestCase }
  const queryOptions = {
    // Cache signed URL for 1 hour - prevents refetching on parent re-renders
    // S3 signed URLs are valid for 24 hours, so 1 hour cache is safe
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
  }

  const publicQuery = usePublicAudioSignedUrlTransport(queryInput, {
    ...queryOptions,
    enabled: isPublic && !!bucket && !!audioKey,
  })
  const privateQuery = useAudioSignedUrlTransport(queryInput, {
    ...queryOptions,
    enabled: !isPublic && !!bucket && !!audioKey,
  })

  const activeQuery = isPublic ? publicQuery : privateQuery
  const signedUrl = activeQuery.data
  const isErrorAudio = activeQuery.isError
  const isLoadingAudio = activeQuery.isLoading

  const containerRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const { wavesurfer, isPlaying, isReady } = useWavesurfer({
    container: containerRef,
    waveColor: "rgb(200, 200, 200)",
    progressColor: "rgb(100, 100, 100)",
    normalize: true,
    url: signedUrl,
    height: compact ? 28 : 100,
  })

  const onPlayPause = useCallback((): void => {
    if (wavesurfer) {
      void wavesurfer.playPause()
    }
  }, [wavesurfer])

  // Seeking on the waveform doesn't naturally focus anything (it's a plain
  // div), so without this the browser's default click-focus fallback would
  // land on the nearest focusable ancestor — the table row — leaving the
  // row's own Enter/Space-to-open handler to fire instead of ours. Focusing
  // the player root keeps Space scoped to this widget regardless of exactly
  // where the click landed.
  useEffect(() => {
    if (!wavesurfer) {
      return
    }

    return wavesurfer.on("interaction", () => {
      rootRef.current?.focus()
    })
  }, [wavesurfer])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === " " || event.code === "Space") {
        event.preventDefault()
        event.stopPropagation()
        onPlayPause()
      }
    },
    [onPlayPause],
  )

  // Show loading state while fetching signed URL OR while wavesurfer is loading
  const isLoading: boolean = isLoadingAudio || (!!signedUrl && !isReady)

  return (
    // The compact root clips the waveform, and the play button sits at its
    // left edge, so the root reaches 2px past its box and pads back in by as
    // much. Content sits where it would without it, and the focus ring drawn
    // 2px outside the button stays inside the clip.
    <div
      role="presentation"
      ref={rootRef}
      className={
        compact
          ? "relative -m-[2px] flex min-w-0 items-center gap-4 overflow-hidden p-[2px]"
          : "relative mx-8"
      }
      // Makes this a valid focus target (see the wavesurfer "interaction"
      // effect above) and marks it as its own interactive island so the
      // table row's own key handling backs off — see
      // `shouldNavigateRow`/`data-prevent-row-click` in @coval/ui's Table.
      tabIndex={-1}
      data-prevent-row-click="true"
      onKeyDown={handleKeyDown}
    >
      {/* Skeleton overlays the container so wavesurfer always has real dimensions */}
      {isLoading && (
        <div
          className={`absolute z-10 flex items-center ${compact ? "inset-[2px] h-[32px] gap-4" : "inset-0 h-[100px] justify-center"}`}
        >
          {compact ? (
            <Skeleton className="w-full h-[18px] rounded-md" />
          ) : (
            <Skeleton className="w-full max-w-md h-[100px] rounded-md" />
          )}
        </div>
      )}
      {compact && signedUrl && wavesurfer && !isLoading && (
        <Tooltip content={isPlaying ? "Pause" : "Play"}>
          <button
            onClick={onPlayPause}
            className="p-1 text-[rgb(200,200,200)]"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4"
              >
                <path
                  fillRule="evenodd"
                  d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4"
              >
                <path
                  fillRule="evenodd"
                  d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </Tooltip>
      )}
      {/* Container always renders with real dimensions so wavesurfer can fire "ready" */}
      {/* `min-w-0` clears the flex item's automatic minimum size: wavesurfer
          renders at whatever width this element has, and without it the
          waveform holds the width it was first drawn at instead of narrowing
          with its column. */}
      <div
        ref={containerRef}
        className={compact ? "min-w-0 flex-1 h-[29px]" : "h-[100px] pt-4"}
      />
      {isErrorAudio && !isLoading && (
        <div className="flex justify-center items-center h-24">
          <p>Error loading audio</p>
        </div>
      )}
      {signedUrl && wavesurfer && !isLoading && (
        <div
          className={`${compact ? "" : "mt-2"} flex justify-center items-center gap-4 relative`}
        >
          {!compact && (
            <Tooltip content={isPlaying ? "Pause" : "Play"}>
              <button
                onClick={onPlayPause}
                className="p-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-8 h-8"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-8 h-8"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}
